import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Save, X } from 'lucide-react';
import { ButtonRow, Field, Input, Label, PrimaryButton, SecondaryButton, Select } from '../AdminUI';
import type {
  CatalogEntity,
  CatalogEvent,
  CatalogEventInput,
  CatalogEventSource,
  CatalogType,
  EntityInput,
  EventSourceInput,
} from '../../../types/catalog';
import {
  FieldGrid,
  FullWidth,
  InlineControl,
  TextArea,
  Toggle,
} from './CatalogUI';
import { breakpoints } from '../../../styles/theme';
import { displayEntityType } from '../../../utils/entityTerminology';
import {
  hasKnownEventTime,
  parseEventLocalDateTime,
  toEventLocalISOString,
} from '../../../utils/eventDateTime';

const SourceFieldGrid = styled(FieldGrid)`
  grid-template-columns: minmax(0, 1fr) 190px;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

interface EntityFormProps {
  entityTypes: CatalogType[];
  entity?: CatalogEntity | null;
  busy?: boolean;
  onSubmit: (input: EntityInput) => Promise<void> | void;
  onCancel?: () => void;
}

export const EntityForm: React.FC<EntityFormProps> = ({
  entityTypes,
  entity,
  busy,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [searchFriendlyName, setSearchFriendlyName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [oneId, setOneId] = useState('');
  const [info, setInfo] = useState('');
  const [contact, setContact] = useState('');
  const [officialDomain, setOfficialDomain] = useState('');
  const [primaryLocationKind, setPrimaryLocationKind] = useState('');
  const [primaryLocationCity, setPrimaryLocationCity] = useState('');
  const [primaryLocationState, setPrimaryLocationState] = useState('');
  const [primaryLocationCountry, setPrimaryLocationCountry] = useState('');
  const [primaryLocationPostalCode, setPrimaryLocationPostalCode] = useState('');
  const [primaryLatitude, setPrimaryLatitude] = useState('');
  const [primaryLongitude, setPrimaryLongitude] = useState('');
  const [primaryLocationSource, setPrimaryLocationSource] = useState('');
  const [eventLocationFallbackEnabled, setEventLocationFallbackEnabled] = useState(false);

  useEffect(() => {
    setName(entity?.entityName ?? '');
    setSearchFriendlyName(entity?.searchFriendlyName ?? '');
    setTypeId(String(entity?.entityTypeId ?? entityTypes[0]?.id ?? ''));
    setOneId(entity?.oneId == null ? '' : String(entity.oneId));
    setInfo(entity?.entityInfo ?? '');
    setContact(entity?.entityContact ?? '');
    setOfficialDomain(entity?.officialDomain ?? '');
    setPrimaryLocationKind(entity?.primaryLocationKind ?? '');
    setPrimaryLocationCity(entity?.primaryLocationCity ?? '');
    setPrimaryLocationState(entity?.primaryLocationState ?? '');
    setPrimaryLocationCountry(entity?.primaryLocationCountry ?? '');
    setPrimaryLocationPostalCode(entity?.primaryLocationPostalCode ?? '');
    setPrimaryLatitude(entity?.primaryLatitude == null ? '' : String(entity.primaryLatitude));
    setPrimaryLongitude(entity?.primaryLongitude == null ? '' : String(entity.primaryLongitude));
    setPrimaryLocationSource(entity?.primaryLocationSource ?? '');
    setEventLocationFallbackEnabled(entity?.eventLocationFallbackEnabled === true);
  }, [entity, entityTypes]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !typeId) return;
    onSubmit({
      entityName: name.trim(),
      searchFriendlyName: searchFriendlyName.trim() || null,
      entityTypeId: Number(typeId),
      oneId: oneId.trim() ? Number(oneId) : null,
      entityInfo: info.trim() || null,
      entityContact: contact.trim() || null,
      officialDomain: officialDomain.trim() || null,
      primaryLocationKind: (primaryLocationKind || null) as EntityInput['primaryLocationKind'],
      primaryLocationCity: primaryLocationCity.trim() || null,
      primaryLocationState: primaryLocationState.trim() || null,
      primaryLocationCountry: primaryLocationCountry.trim() || null,
      primaryLocationPostalCode: primaryLocationPostalCode.trim() || null,
      primaryLatitude: primaryLatitude.trim() ? Number(primaryLatitude) : null,
      primaryLongitude: primaryLongitude.trim() ? Number(primaryLongitude) : null,
      primaryLocationSource: primaryLocationSource.trim() || null,
      primaryLocationVerifiedAt: eventLocationFallbackEnabled
        ? new Date().toISOString()
        : entity?.primaryLocationVerifiedAt ?? null,
      eventLocationFallbackEnabled,
    });
  };

  return (
    <form onSubmit={submit}>
      <FieldGrid>
        <Field>
          <Label htmlFor="catalog-entity-name">Name</Label>
          <Input
            id="catalog-entity-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={255}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-search-name">Search-friendly name</Label>
          <Input
            id="catalog-entity-search-name"
            value={searchFriendlyName}
            onChange={(event) => setSearchFriendlyName(event.target.value)}
            maxLength={255}
            placeholder="Generated before the next WebIQ run"
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-type">Type</Label>
          <Select
            id="catalog-entity-type"
            value={typeId}
            onChange={(event) => setTypeId(event.target.value)}
            required
          >
            <option value="">Choose a type</option>
            {entityTypes.map((type) => (
              <option key={type.id} value={type.id}>{displayEntityType(type.name)}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-one-id">One ID</Label>
          <Input
            id="catalog-entity-one-id"
            type="number"
            min={1}
            value={oneId}
            onChange={(event) => setOneId(event.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-contact">Contact</Label>
          <Input
            id="catalog-entity-contact"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            maxLength={255}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-domain">Official domain</Label>
          <Input
            id="catalog-entity-domain"
            value={officialDomain}
            onChange={(event) => setOfficialDomain(event.target.value)}
            maxLength={255}
            placeholder="example.edu"
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-kind">Primary location kind</Label>
          <Select
            id="catalog-entity-location-kind"
            value={primaryLocationKind}
            onChange={(event) => setPrimaryLocationKind(event.target.value)}
          >
            <option value="">No primary location</option>
            <option value="MAIN_CAMPUS">Main campus</option>
            <option value="CITY_CENTER">City center</option>
          </Select>
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-city">Primary city</Label>
          <Input
            id="catalog-entity-location-city"
            value={primaryLocationCity}
            onChange={(event) => setPrimaryLocationCity(event.target.value)}
            maxLength={255}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-state">Primary state or region</Label>
          <Input
            id="catalog-entity-location-state"
            value={primaryLocationState}
            onChange={(event) => setPrimaryLocationState(event.target.value)}
            maxLength={255}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-country">Primary country</Label>
          <Input
            id="catalog-entity-location-country"
            value={primaryLocationCountry}
            onChange={(event) => setPrimaryLocationCountry(event.target.value)}
            maxLength={255}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-postal">Primary postal code</Label>
          <Input
            id="catalog-entity-location-postal"
            value={primaryLocationPostalCode}
            onChange={(event) => setPrimaryLocationPostalCode(event.target.value)}
            maxLength={20}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-latitude">Primary latitude</Label>
          <Input
            id="catalog-entity-latitude"
            type="number"
            min={-90}
            max={90}
            step="any"
            value={primaryLatitude}
            onChange={(event) => setPrimaryLatitude(event.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-longitude">Primary longitude</Label>
          <Input
            id="catalog-entity-longitude"
            type="number"
            min={-180}
            max={180}
            step="any"
            value={primaryLongitude}
            onChange={(event) => setPrimaryLongitude(event.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-entity-location-source">Location source</Label>
          <Input
            id="catalog-entity-location-source"
            value={primaryLocationSource}
            onChange={(event) => setPrimaryLocationSource(event.target.value)}
            maxLength={50}
            placeholder="IPEDS"
          />
        </Field>
        <FullWidth>
          <InlineControl>
            <Toggle
              checked={eventLocationFallbackEnabled}
              onChange={(event) => setEventLocationFallbackEnabled(event.target.checked)}
            />
            Use this verified primary location when an eligible hosted event has no address
          </InlineControl>
        </FullWidth>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-entity-info">Entity info</Label>
            <TextArea
              id="catalog-entity-info"
              value={info}
              onChange={(event) => setInfo(event.target.value)}
              maxLength={2000}
              placeholder="JSON or descriptive metadata"
            />
          </Field>
        </FullWidth>
      </FieldGrid>
      <ButtonRow>
        <PrimaryButton type="submit" disabled={busy || !name.trim() || !typeId}>
          <Save size={16} /> {entity ? 'Save entity' : 'Create entity'}
        </PrimaryButton>
        {onCancel && (
          <SecondaryButton type="button" onClick={onCancel} disabled={busy}>
            <X size={16} /> Cancel
          </SecondaryButton>
        )}
      </ButtonRow>
    </form>
  );
};

interface EventSourceFormProps {
  eventSourceTypes: CatalogType[];
  eventSource?: CatalogEventSource | null;
  busy?: boolean;
  onSubmit: (input: EventSourceInput) => Promise<void> | void;
  onCancel?: () => void;
}

export const EventSourceForm: React.FC<EventSourceFormProps> = ({
  eventSourceTypes,
  eventSource,
  busy,
  onSubmit,
  onCancel,
}) => {
  const [typeId, setTypeId] = useState('');
  const [source, setSource] = useState('');
  const [sourceInfo, setSourceInfo] = useState('');
  const [promptFile, setPromptFile] = useState('');

  useEffect(() => {
    const websiteType = eventSourceTypes.find((type) => type.name === 'Website');
    setTypeId(String(eventSource?.eventSourceTypeId ?? websiteType?.id ?? eventSourceTypes[0]?.id ?? ''));
    setSource(eventSource?.source ?? '');
    setSourceInfo(eventSource?.sourceInfo ?? '');
    setPromptFile(eventSource?.promptFile ?? '');
  }, [eventSource, eventSourceTypes]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!source.trim() || !typeId) return;
    onSubmit({
      eventSourceTypeId: Number(typeId),
      source: source.trim(),
      sourceInfo: sourceInfo.trim() || null,
      promptFile: promptFile || null,
    });
  };

  return (
    <form onSubmit={submit}>
      <SourceFieldGrid>
        <Field>
          <Label htmlFor="catalog-source-value">Source</Label>
          <Input
            id="catalog-source-value"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            maxLength={2083}
            placeholder="https://example.com/events"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-source-type">Type</Label>
          <Select
            id="catalog-source-type"
            value={typeId}
            onChange={(event) => setTypeId(event.target.value)}
            required
          >
            <option value="">Choose a type</option>
            {eventSourceTypes.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </Select>
        </Field>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-source-info">Source info</Label>
            <TextArea
              id="catalog-source-info"
              value={sourceInfo}
              onChange={(event) => setSourceInfo(event.target.value)}
              maxLength={1000}
            />
          </Field>
        </FullWidth>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-source-prompt">Prompt file</Label>
            <TextArea
              id="catalog-source-prompt"
              value={promptFile}
              onChange={(event) => setPromptFile(event.target.value)}
              placeholder="Optional source-specific extraction prompt"
            />
          </Field>
        </FullWidth>
      </SourceFieldGrid>
      <ButtonRow>
        <PrimaryButton type="submit" disabled={busy || !source.trim() || !typeId}>
          <Save size={16} /> {eventSource ? 'Save source' : 'Create source'}
        </PrimaryButton>
        {onCancel && (
          <SecondaryButton type="button" onClick={onCancel} disabled={busy}>
            <X size={16} /> Cancel
          </SecondaryButton>
        )}
      </ButtonRow>
    </form>
  );
};

function toDateInput(value?: string | null): string {
  return toEventLocalISOString(value).slice(0, 10);
}

function toTimeInput(value?: string | null): string {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed || !hasKnownEventTime(value)) return '';
  return `${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`;
}

function combineEventDateTime(date: string, time: string): string {
  return time ? `${date}T${time}:00` : date;
}

interface EventFormProps {
  event: CatalogEvent;
  busy?: boolean;
  onSubmit: (input: CatalogEventInput) => Promise<void> | void;
}

export const EventForm: React.FC<EventFormProps> = ({ event, busy, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [type, setType] = useState('');
  const [venue, setVenue] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [eventUrl, setEventUrl] = useState('');
  const [isVirtual, setIsVirtual] = useState(false);
  const [isPartnerHosted, setIsPartnerHosted] = useState(false);

  useEffect(() => {
    setTitle(event.title ?? '');
    setDescription(event.description ?? '');
    setStartDate(toDateInput(event.startDate));
    setStartTime(toTimeInput(event.startDate));
    setEndDate(toDateInput(event.endDate));
    setEndTime(toTimeInput(event.endDate));
    setType(event.type ?? '');
    setVenue(event.venue ?? '');
    setCity(event.city ?? '');
    setState(event.state ?? '');
    setCountry(event.country ?? '');
    setEventUrl(event.eventUrl ?? '');
    setIsVirtual(event.isVirtual);
    setIsPartnerHosted(event.isPartnerHosted);
  }, [event]);

  const submit = (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (!title.trim() || !startDate || !endDate) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      startDate: combineEventDateTime(startDate, startTime),
      endDate: combineEventDateTime(endDate, endTime),
      type: type.trim() || null,
      venue: venue.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      country: country.trim() || null,
      eventUrl: eventUrl.trim() || null,
      isVirtual,
      isPartnerHosted,
    });
  };

  return (
    <form onSubmit={submit}>
      <FieldGrid>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-event-title">Title</Label>
            <Input
              id="catalog-event-title"
              value={title}
              onChange={(formEvent) => setTitle(formEvent.target.value)}
              maxLength={255}
              required
            />
          </Field>
        </FullWidth>
        <Field>
          <Label htmlFor="catalog-event-start-date">Start date</Label>
          <Input
            id="catalog-event-start-date"
            type="date"
            value={startDate}
            onChange={(formEvent) => setStartDate(formEvent.target.value)}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-start-time">Start time (optional)</Label>
          <Input
            id="catalog-event-start-time"
            type="time"
            value={startTime}
            onChange={(formEvent) => setStartTime(formEvent.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-end-date">End date</Label>
          <Input
            id="catalog-event-end-date"
            type="date"
            value={endDate}
            onChange={(formEvent) => setEndDate(formEvent.target.value)}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-end-time">End time (optional)</Label>
          <Input
            id="catalog-event-end-time"
            type="time"
            value={endTime}
            onChange={(formEvent) => setEndTime(formEvent.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-type">Event type</Label>
          <Input id="catalog-event-type" value={type} onChange={(e) => setType(e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-venue">Venue</Label>
          <Input id="catalog-event-venue" value={venue} onChange={(e) => setVenue(e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-city">City</Label>
          <Input id="catalog-event-city" value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-state">State</Label>
          <Input id="catalog-event-state" value={state} onChange={(e) => setState(e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="catalog-event-country">Country</Label>
          <Input id="catalog-event-country" value={country} onChange={(e) => setCountry(e.target.value)} />
        </Field>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-event-url">Event URL</Label>
            <Input id="catalog-event-url" value={eventUrl} onChange={(e) => setEventUrl(e.target.value)} />
          </Field>
        </FullWidth>
        <FullWidth>
          <Field>
            <Label htmlFor="catalog-event-description">Description</Label>
            <TextArea
              id="catalog-event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </FullWidth>
        <InlineControl>
          <Toggle checked={isVirtual} onChange={(e) => setIsVirtual(e.target.checked)} />
          Virtual event
        </InlineControl>
        <InlineControl>
          <Toggle
            checked={isPartnerHosted}
            onChange={(e) => setIsPartnerHosted(e.target.checked)}
          />
          Tech company hosted
        </InlineControl>
      </FieldGrid>
      <ButtonRow>
        <PrimaryButton type="submit" disabled={busy || !title.trim() || !startDate || !endDate}>
          <Save size={16} /> Save event
        </PrimaryButton>
      </ButtonRow>
    </form>
  );
};